import { useQuery, type QueryClient } from '@tanstack/react-query';

import { authApi, sessionFromMe } from '@/entities/auth';
import { organisationKeys } from '@/entities/order';
import { hasPermission, type Permission } from '@/entities/user';
import { isRole, useSessionStore, type Role } from '@/shared/api';

export interface WorkspaceAccess {
    /** Mag de persoon achter de sessie dit hier? */
    can: (permission: Permission) => boolean;
    /** Wie de handelingen doet; bij meekijken is dat de ander. */
    userUuid: string | null;
    /** De naam van diezelfde persoon, voor "Mijzelf" in een keuze. */
    userName: string | null;
    /** Zijn rollen in deze omgeving. */
    roles: Role[];
    /** Tijdens meekijken zijn de rechten van de ander nog onderweg. */
    loading: boolean;
}

/** Waar de rechten van degene op wie wordt meegekeken in de cache staan. */
export const workspaceAccessKeys = {
    all: ['meekijken', 'rechten'] as const,
    impersonated: (tenantId: string | undefined, userName: string | undefined) =>
        ['meekijken', 'rechten', tenantId, userName] as const,
};

/**
 * De persoon en zijn rechten in deze omgeving, uit de sessie (`user`, `roles`,
 * `permissions`).
 *
 * Kijkt een platformbeheerder mee, dan doet hij alles als de ander. Zijn eigen
 * sessie zegt daar niets over, dus dan vragen we `GET /auth/me` met het
 * tijdelijke token: die rechten en die persoon gelden dan.
 *
 * Dit bepaalt alleen wat het scherm aanbiedt. De API beslist, en weigert wat
 * iemand niet mag - een knop die er niet hoort te zijn, staat er dan ook niet.
 */
export function useWorkspaceAccess(): WorkspaceAccess {
    const session = useSessionStore((state) => state.session);
    const impersonation = useSessionStore((state) => state.impersonation);

    const impersonated = useQuery({
        queryKey: workspaceAccessKeys.impersonated(
            impersonation?.tenantId,
            impersonation?.userName,
        ),
        queryFn: () => authApi.me({ token: impersonation?.token ?? '' }),
        enabled: impersonation !== null,
        staleTime: Infinity,
    });

    if (impersonation) {
        const me = impersonated.data;
        const permissions = me?.permissions ?? [];

        return {
            can: (permission) => hasPermission(permissions, permission),
            userUuid: me?.user.uuid ?? null,
            userName: me?.user.name ?? null,
            roles: (me?.roles ?? []).filter(isRole),
            loading: impersonated.isPending,
        };
    }

    const permissions = session?.permissions ?? [];

    return {
        can: (permission) => hasPermission(permissions, permission),
        userUuid: session?.user.uuid ?? null,
        userName: session?.user.name ?? null,
        roles: session?.roles ?? [],
        loading: false,
    };
}

/**
 * Rollen en rechten opnieuw uit `GET /auth/me`, met hetzelfde token, en daarmee ook
 * de naam en het logo van de omgeving in de kopbalk en de rail.
 *
 * Kijkt een platformbeheerder mee, dan staan die van de ander in de querycache
 * (`useWorkspaceAccess`); anders in de sessie. Is de sessie intussen gewisseld,
 * dan hoort het antwoord bij een oude sessie en blijft het liggen. Kent de sessie
 * geen omgeving, dan leest de kopbalk `GET /tenant` (`useOrganisation`); die vraagt
 * hij dan opnieuw.
 */
export async function reloadWorkspaceSession(client: QueryClient): Promise<void> {
    const { session, impersonation } = useSessionStore.getState();

    void client.invalidateQueries({ queryKey: organisationKeys.current() });

    if (impersonation) {
        await client.invalidateQueries({ queryKey: workspaceAccessKeys.all });

        return;
    }

    if (!session) {
        return;
    }

    const me = await authApi.me(session);

    if (useSessionStore.getState().session?.token !== session.token) {
        return;
    }

    await useSessionStore.getState().signIn(sessionFromMe(me, session));
}
